import type { LucideIcon } from 'lucide-react'
import {
  CalendarDays,
  Columns3,
  Code,
  Filter,
  Clock,
  Link,
  Lock,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Search,
  Settings,
  Settings2,
  ListPlus,
  LayoutDashboard,
  ArrowUpDown,
  Waypoints,
} from 'lucide-react'
import type { ComponentType } from '@/types/schemas/workspace'
import { EDGESCRIPT_ENABLED } from '@/features/edgescript/flags'
import type { Feature, PlanTier } from '@/context/plan-constraint-utils'
import { usePlanConstraint } from '@/context/plan-constraint-hooks'
import {
  FEATURE_REQUIREMENTS,
  PLAN_LABELS,
  tierMeetsRequirement,
} from '@/context/plan-constraint-utils'
import { openUpgradePrompt } from '@/features/upgrade/upgrade-prompt-store'
import { useNotificationStore } from '@/stores/notification-store'
import { trackEvent, EVENTS } from '@/services/analytics'
import { FLAGS } from '@/services/analytics/feature-flags'
import { useFeatureFlag } from '@/services/analytics/use-feature-flag'
import type { LinkMode } from '../types'
import { PANEL_ROLLOUT_FLAGS } from '../panel-rollout'
import { PanelLinkMenu } from './panel-link-menu'
import { RolloutFlag } from './rollout-gate'
import { useDevice } from '@/context/device-hooks'

/**
 * The panel title bar's icon glyph: every chrome action (Settings, Link, ...)
 * and every icon-only header control (AlertToolbarButton) draws at this size
 * and stroke, so a panel's header icons read as one set.
 */
export const PANEL_ICON_SIZE = 15
export const PANEL_ICON_STROKE = 1.8

// ─── Types ───

interface PanelAction {
  id: string
  icon: LucideIcon
  title: string
  /** Minimum plan tier required. If omitted, available to all. */
  requiredPlan?: PlanTier
  /**
   * The gate this action sits behind. When set, access is decided by the
   * SERVER's granted feature list rather than by comparing tiers, and a refusal
   * raises the shared upgrade pitch. `requiredPlan` stays as the offline
   * fallback for actions that never named a feature.
   */
  feature?: Feature
}

// ─── Master action lookup (all possible actions by ID) ───

const ACTION_DEFS: Record<string, PanelAction> = {
  columns: { id: 'columns', icon: Columns3, title: 'Columns' },
  filters: { id: 'filters', icon: Filter, title: 'Filters' },
  history: { id: 'history', icon: Clock, title: 'History' },
  link: { id: 'link', icon: Link, title: 'Link' },
  search: { id: 'search', icon: Search, title: 'Search' },
  aggregates: {
    id: 'aggregates',
    icon: Settings2,
    title: 'Aggregates',
    requiredPlan: 'pro',
    feature: 'ticker-stats-custom',
  },
  'watchlist-manager': { id: 'watchlist-manager', icon: ListPlus, title: 'Watchlist Manager' },
  settings: { id: 'settings', icon: Settings, title: 'Settings' },
  fullscreen: { id: 'fullscreen', icon: Maximize2, title: 'Fullscreen' },
  // Same toggle, reversed icon — panels register this override while maximized.
  'fullscreen-exit': { id: 'fullscreen-exit', icon: Minimize2, title: 'Exit fullscreen' },
  stages: { id: 'stages', icon: Waypoints, title: 'Stages' },
  layout: { id: 'layout', icon: LayoutDashboard, title: 'Layout' },
  sort: { id: 'sort', icon: ArrowUpDown, title: 'Sort' },
  pause: { id: 'pause', icon: Pause, title: 'Pause auto-refresh' },
  resume: { id: 'resume', icon: Play, title: 'Resume auto-refresh' },
  'calendar-toggle': { id: 'calendar-toggle', icon: CalendarDays, title: 'Toggle mini calendar' },
  // EdgeScript (dev-gated): the script editor docked under the chart. Its own
  // header icon, not a picker entry, because a script is a workspace of its
  // own and bigger than one indicator (Chris, 2026-09-06).
  script: { id: 'script', icon: Code, title: 'Script' },
}

/**
 * The plan an action pitches, as sold. Derived, never hardcoded: the feature's
 * own requirement when it names one, else the action's tier floor.
 */
function requiredPlanLabel(action: PanelAction): string | null {
  const tier = action.feature ? FEATURE_REQUIREMENTS[action.feature] : action.requiredPlan
  return tier ? PLAN_LABELS[tier] : null
}

// ─── Per-component-type default action definitions ───

const PANEL_ACTIONS: Partial<Record<ComponentType, PanelAction[]>> = {
  scanner: [ACTION_DEFS['settings'], ACTION_DEFS['link']],
  // Heatmap: Settings opens the universe filter sheet; ticker clicks broadcast
  // via the panel's link color.
  'sector-heatmap': [ACTION_DEFS['settings'], ACTION_DEFS['link']],
  'multi-scanner': [ACTION_DEFS['settings'], ACTION_DEFS['link']],
  // Fullscreen sits right after the header's eye toggle (chart maximize moved
  // out of the chart's own toolbar).
  chart: [
    ACTION_DEFS['layout'],
    ACTION_DEFS['fullscreen'],
    ...(EDGESCRIPT_ENABLED ? [ACTION_DEFS['script']] : []),
    ACTION_DEFS['link'],
  ],
  'key-stats': [ACTION_DEFS['settings'], ACTION_DEFS['link']],
  // Watchlist Manager moved into the header dropdown's "Manage watchlists"
  // entry, so the hover-hidden chrome icon is gone (Chris, 2026-08-20).
  watchlist: [ACTION_DEFS['settings'], ACTION_DEFS['link']],
  // Settings lives in the header swap slot (ThemeTrackerHeaderControls), shown only
  // in Table view, so it is NOT in the standard chrome cluster here (Chris, 2026-08-19).
  'theme-tracker': [ACTION_DEFS['link']],
  // No separate Sources button: the Settings sheet carries a Sources tab, so the
  // chrome icon was a second door onto the same room (Chris, 2026-08-27).
  news: [ACTION_DEFS['settings'], ACTION_DEFS['link']],
  // One Settings gear opens a single tabbed sheet (Filters + Aggregates on the
  // Stats view; Filters + Columns + Colors on the Records view) — the canonical
  // scanner pattern. The aggregates pro-gate is preserved on the Aggregates tab.
  'ticker-stats': [ACTION_DEFS['settings'], ACTION_DEFS['link']],
  calendar: [ACTION_DEFS['settings'], ACTION_DEFS['link']],
  'history-records': [ACTION_DEFS['settings']],
  'tags-explorer': [ACTION_DEFS['settings']],
  'history-stats': [ACTION_DEFS['aggregates']],
  // Scorecard's edit affordance is the metric-config gear. In SCROLL mode it
  // rides the ScorecardHeader override (via renderGridHeaderOverride); the
  // MOSAIC path renders the generic PanelHeader, which reads this map — so
  // without an entry the gear was missing in mosaic. Same 'aggregates' handler
  // (registered by use-chart-aggregate-panel) as every sibling aggregate panel.
  'history-scorecard': [ACTION_DEFS['aggregates']],
  // Same ChartGrid surface as `chart` — ChartGrid registers its live action
  // list (fullscreen ⇄ fullscreen-exit + link) at mount; this is the fallback.
  'history-chart': [ACTION_DEFS['layout'], ACTION_DEFS['fullscreen'], ACTION_DEFS['link']],
  // The backtest report's trade chart is that same surface again.
  'backtest-trade-chart': [ACTION_DEFS['layout'], ACTION_DEFS['fullscreen'], ACTION_DEFS['link']],
  // HistoryNewsPanel IS NewsPanel, so it loses the redundant Sources icon too.
  // The Search icon is dropped too: NewsPanel has no search handler, so it was a dead button.
  'history-news': [ACTION_DEFS['settings']],
  // Comparison is a table, not a chart — its config trigger reads as a settings
  // gear (the chart panels keep the distinct "aggregates" sliders icon). Same
  // 'aggregates' action id, so the panel's toolbar handler is unchanged.
  'history-comparison': [{ ...ACTION_DEFS['aggregates'], icon: Settings, title: 'Settings' }],
  'history-time-series': [ACTION_DEFS['aggregates']],
  'history-scatter': [ACTION_DEFS['aggregates']],
  'history-bar': [ACTION_DEFS['aggregates']],
  'history-bar-category': [ACTION_DEFS['aggregates']],
  'history-donut-category': [ACTION_DEFS['aggregates']],
  // View + Weekends moved to a visible header dropdown (HistoryCalendarViewControl,
  // via renderToolbarExtrasRight); with the density gradient fixed, nothing is
  // left to configure behind a gear, so the calendar header carries no actions.
  'history-calendar': [],
  'history-sankey': [ACTION_DEFS['stages']],
  // Category dimension + Color + Size live in the header pill
  // (HistoryHeatmapHeaderControls); the heatmap reads the shared page
  // filters, so there is no per-panel Settings sheet — only Link.
  'history-heatmap': [ACTION_DEFS['link']],
  // Reference Day + Extreme live in the header pill
  // (HistoryTimeDistributionHeaderControls); the panel reads the shared page
  // filters, so there is no per-panel Settings sheet, only Link.
  'history-time-distribution': [ACTION_DEFS['link']],
  notebook: [ACTION_DEFS['link']],
  // Gear opens Alert Window Config, as on the Scanner.
  'alert-windows': [ACTION_DEFS['settings'], ACTION_DEFS['link']],
  // Settings stays ON the chart (active-cell gear); the header carries
  // Filters, Layout, Fullscreen, and Link. The panel registers these
  // dynamically (fullscreen ⇄ fullscreen-exit); this static list is the fallback.
  'multi-chart': [
    ACTION_DEFS['filters'],
    ACTION_DEFS['layout'],
    ACTION_DEFS['fullscreen'],
    ACTION_DEFS['link'],
  ],
}

// ─── Component ───

interface PanelActionButtonsProps {
  componentType: ComponentType
  linkMode: LinkMode
  onLinkModeChange: (mode: LinkMode) => void
  /** Anchor = the click's viewport point, for actions that open an anchored popover. */
  onAction?: (actionId: string, anchor?: { x: number; y: number }) => void
  /** Override the default action list for this component type. Array of action IDs. */
  actionOverrides?: string[]
  /** Badge counts to display on action buttons (e.g. { filters: 7 }). */
  actionBadges?: Record<string, number>
}

export function PanelActionButtons({
  componentType,
  linkMode,
  onLinkModeChange,
  onAction,
  actionOverrides,
  actionBadges,
}: PanelActionButtonsProps) {
  const { planTier, hasAccess } = usePlanConstraint()
  const { isMobile } = useDevice()
  // EdgeScript's Script action rides `edgescript-release` inside the build gate, in
  // the default list and in a list the panel registered (chart-grid) alike.
  const edgeScriptReleased = useFeatureFlag(FLAGS.EDGESCRIPT)
  const scriptActionOn = EDGESCRIPT_ENABLED && edgeScriptReleased
  const allActions = (
    actionOverrides
      ? actionOverrides.map((id) => ACTION_DEFS[id]).filter(Boolean)
      : (PANEL_ACTIONS[componentType] ?? [])
  ).filter((a) => a.id !== 'script' || scriptActionOn)
  // Hide link button on mobile — all panels are always onChange-linked
  const actions = isMobile ? allActions.filter((a) => a.id !== 'link') : allActions

  // A flagged panel type's chrome hides with its body while the flag is off (its
  // RolloutGate). Only flagged types read their flag; the rest subscribe to nothing.
  const flag = PANEL_ROLLOUT_FLAGS[componentType]

  if (actions.length === 0) return null

  const buttons = (
    <>
      {actions.map((action) => {
        if (action.id === 'link') {
          return (
            <PanelLinkMenu
              key={action.id}
              linkMode={linkMode}
              componentType={componentType}
              onLinkModeChange={onLinkModeChange}
            />
          )
        }

        // An action that names a feature is gated by what the server actually
        // granted; one that only declares a tier keeps the offline comparison.
        const isGated = action.feature
          ? !hasAccess(action.feature)
          : action.requiredPlan != null && !tierMeetsRequirement(planTier, action.requiredPlan)
        const Icon = action.icon
        return (
          <button
            key={action.id}
            // Every action keeps a PANEL-IDENTIFYING label (e.g. `scanner-settings`,
            // `key-stats-settings`) so the Companion's pointing resolves to the
            // right panel — a shared label would make first-match point at
            // whichever panel is first in the DOM. The guided walkthrough finds
            // the settings gear via the separate `data-panel-settings` marker
            // below (scoped to the panel it's anchored to), so it doesn't need a
            // shared label.
            data-companion-label={`${componentType}-${action.id}`}
            data-action={action.id}
            data-panel-settings={action.id === 'settings' ? '' : undefined}
            title={
              isGated && requiredPlanLabel(action)
                ? `${action.title} (${requiredPlanLabel(action)})`
                : action.title
            }
            onClick={(e) => {
              if (isGated) {
                // The shared pitch owns the copy AND the UPGRADE_PROMPT_SHOWN
                // event, so the call site only names the gate that refused.
                if (action.feature) {
                  openUpgradePrompt({ feature: action.feature, reason: 'locked' })
                  return
                }
                // An action gated only by tier has no pitch to open yet, so it
                // keeps the toast it always had.
                trackEvent(EVENTS.UPGRADE_PROMPT_SHOWN, {
                  feature: action.id,
                  component_type: componentType,
                })
                useNotificationStore
                  .getState()
                  .showInfo(`Upgrade to ${requiredPlanLabel(action)} to use this feature`)
                return
              }
              // Anchor from the BUTTON's rect (not the click point) so a popover
              // opened by this action aligns under the button's right edge.
              const r = e.currentTarget.getBoundingClientRect()
              onAction?.(action.id, { x: r.right, y: r.bottom + 4 })
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="relative flex h-6 w-6 items-center justify-center rounded border-none bg-transparent text-muted-foreground hover:bg-hover hover:text-accent-foreground"
            style={{
              padding: 0,
              cursor: isGated ? 'not-allowed' : 'pointer',
              opacity: isGated ? 0.5 : 1,
              flexShrink: 0,
            }}
          >
            <Icon size={PANEL_ICON_SIZE} strokeWidth={PANEL_ICON_STROKE} />
            {isGated && (
              <Lock
                size={9}
                strokeWidth={2.5}
                className="absolute -bottom-0.5 -right-0.5 text-muted-foreground"
              />
            )}
            {!isGated && actionBadges?.[action.id] != null && actionBadges[action.id] > 0 && (
              <span className="absolute top-0 right-0 flex h-3 min-w-[12px] items-center justify-center rounded-full bg-primary px-[3px] text-[7px] font-bold leading-none text-primary-foreground">
                {actionBadges[action.id]}
              </span>
            )}
          </button>
        )
      })}
    </>
  )
  return flag ? <RolloutFlag flag={flag}>{(on) => (on ? buttons : null)}</RolloutFlag> : buttons
}
